import axios from "axios";
import { BrandMapping } from "../models/BrandMapping";

/**
 * RAG-31 — Correspondances de marques.
 *   - GET  admins/mappings/brands        -> liste paginée + compteurs par onglet
 *   - PUT  admins/mappings/brands/{id}    -> rapproche (fixe la marque cible)
 *   - GET  admins/brands/search?q=        -> recherche de marques pour le sélecteur
 */

export interface BrandMappingCounts {
  unmapped: number;
  auto: number;
  manual: number;
  total: number;
}

export interface BrandMappingsList {
  mappings: BrandMapping[];
  counts: BrandMappingCounts;
  currentPage: number;
  lastPage: number;
}

export interface BrandOption {
  id: string;
  name: string;
}

function messageForStatus(error: any): string {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.error ?? error?.response?.data?.message;
  switch (status) {
    case 403:
      return "Vous n'avez pas les droits pour modifier les correspondances.";
    case 422:
      return apiMessage || "Requête invalide.";
    default:
      return "Veuillez contacter un administrateur";
  }
}

function list(status = "all", source = "", page = 1): Promise<BrandMappingsList> {
  const params = new URLSearchParams();
  params.set("status", status);
  params.set("page", String(page));
  if (source) {
    params.set("source", source);
  }

  return axios
    .get(`${process.env.REACT_APP_API_URL}admins/mappings/brands?${params.toString()}`)
    .then((response) => ({
      mappings: (response.data?.data?.data ?? []).map((m: any) => new BrandMapping(m)),
      counts: response.data?.counts ?? { unmapped: 0, auto: 0, manual: 0, total: 0 },
      currentPage: response.data?.data?.current_page ?? 1,
      lastPage: response.data?.data?.last_page ?? 1,
    }));
}

function update(id: number, brandId: string | null): Promise<void> {
  return new Promise((resolve, reject) => {
    axios
      .put(`${process.env.REACT_APP_API_URL}admins/mappings/brands/${id}`, { brand_id: brandId })
      .then(() => resolve())
      .catch((error) => reject({ status: error?.response?.status, message: messageForStatus(error) }));
  });
}

function searchBrands(term: string): Promise<BrandOption[]> {
  return axios
    .get(`${process.env.REACT_APP_API_URL}admins/brands/search?q=${encodeURIComponent(term)}`)
    .then((response) => response.data?.data ?? []);
}

/**
 * RAG-66 — Crée une marque Ragues depuis une correspondance non rapprochée puis la
 * relie. Résout avec brandCreated=false si une marque équivalente existait déjà
 * (réutilisée au lieu de créer un doublon).
 */
function createBrand(id: number, name?: string): Promise<{ brandCreated: boolean }> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_API_URL}admins/mappings/brands/${id}/create-brand`, name ? { name } : {})
      .then((response) => resolve({ brandCreated: !!response.data?.brand_created }))
      .catch((error) => reject({ status: error?.response?.status, message: messageForStatus(error) }));
  });
}

export default {
  list,
  update,
  searchBrands,
  createBrand,
};
